import React, { useState, useEffect } from 'react';
import { Offcanvas, Modal, Button, Table, Badge, Form } from 'react-bootstrap';
import { X as FiX } from 'react-bootstrap-icons';
import { Order } from '../../models/order';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import { resolveOrderProfitMetrics } from '../../utils/orderProfitDisplay';
import styles from './OrderView.module.scss';

export type DeliveryNoteAmountCurrencyMode =
  | 'referenceCurrency'
  | 'organizationCurrency'
  | 'both';

export interface DeliveryNotePrintOptions {
  showAmounts: boolean;
  amountCurrencyMode: DeliveryNoteAmountCurrencyMode;
  /** Visual only: hide Desc. % column; montos siguen con descuento aplicado */
  showLineDiscountColumn: boolean;
}

export interface DeliveryNoteSendOptions extends DeliveryNotePrintOptions {}

interface OrderViewProps {
  show: boolean;
  onClose: () => void;
  onExited?: () => void;
  order: Order | null;
  referenceCurrency?: string;
  currency?: string;
  /** Preloaded from saved delivery note design (sandbox). Used as initial values for print/send. */
  defaultDeliveryNoteShowAmounts?: boolean;
  defaultDeliveryNoteAmountCurrencyMode?: DeliveryNoteAmountCurrencyMode;
  /** Default true when omitted (resolver applies legacy layout keys) */
  defaultDeliveryNoteShowLineDiscountColumn?: boolean;
  onPrepareDeliveryNoteRecipient?: (order: Order) => Promise<string>;
  onSendDeliveryNote?: (
    order: Order,
    recipientEmail: string,
    options: DeliveryNoteSendOptions
  ) => Promise<void>;
  onPrintDeliveryNote?: (order: Order, options: DeliveryNotePrintOptions) => void;
  onMarkAsInvoiced?: (order: Order) => Promise<void>;
  /**
   * When the sale is already invoiced, keep "Facturado" actionable to retry Profit inventory sync
   * (same handler shows a confirmation in the parent).
   */
  isProfitInventoryRetryAvailable?: (order: Order) => boolean;
}

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function formatDate(date: any): string {
  if (!date) return 'N/A';

  try {
    let dateObj: Date;

    // Handle Firestore timestamp format
    if (date && typeof date === 'object') {
      // Check if it's a Firestore Timestamp with toDate method
      if (typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      // Handle Firestore timestamp: { seconds: number, nanoseconds: number }
      else if ('seconds' in date) {
        dateObj = new Date((date as any).seconds * 1000);
      }
      // Handle Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Try to convert to Date
      else {
        dateObj = new Date(date);
      }
    }
    // Handle string or number
    else {
      dateObj = new Date(date);
    }

    if (isNaN(dateObj.getTime())) return 'N/A';

    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return 'N/A';
  }
}

const OrderView: React.FC<OrderViewProps> = ({
  show,
  onClose,
  onExited,
  order,
  referenceCurrency = 'USD',
  currency = 'VES',
  defaultDeliveryNoteShowAmounts,
  defaultDeliveryNoteAmountCurrencyMode,
  defaultDeliveryNoteShowLineDiscountColumn,
  onPrepareDeliveryNoteRecipient,
  onSendDeliveryNote,
  onPrintDeliveryNote,
  onMarkAsInvoiced,
  isProfitInventoryRetryAvailable,
}) => {
  const [sendingDeliveryNote, setSendingDeliveryNote] = useState(false);
  const [markingInvoiced, setMarkingInvoiced] = useState(false);
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [printShowAmounts, setPrintShowAmounts] = useState<boolean>(false);
  const [printAmountCurrencyMode, setPrintAmountCurrencyMode] =
    useState<DeliveryNoteAmountCurrencyMode>('referenceCurrency');
  const [showSendSettings, setShowSendSettings] = useState(false);
  const [loadingRecipientEmail, setLoadingRecipientEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientEmailTouched, setRecipientEmailTouched] = useState(false);
  const [sendShowAmounts, setSendShowAmounts] = useState<boolean>(false);
  const [sendAmountCurrencyMode, setSendAmountCurrencyMode] =
    useState<DeliveryNoteAmountCurrencyMode>('referenceCurrency');
  const [printShowLineDiscountColumn, setPrintShowLineDiscountColumn] =
    useState(true);
  const [sendShowLineDiscountColumn, setSendShowLineDiscountColumn] =
    useState(true);

  const savedShowAmounts =
    typeof defaultDeliveryNoteShowAmounts === 'boolean'
      ? defaultDeliveryNoteShowAmounts
      : false;
  const savedAmountMode =
    defaultDeliveryNoteAmountCurrencyMode === 'referenceCurrency' ||
    defaultDeliveryNoteAmountCurrencyMode === 'organizationCurrency' ||
    defaultDeliveryNoteAmountCurrencyMode === 'both'
      ? defaultDeliveryNoteAmountCurrencyMode
      : 'referenceCurrency';
  const savedShowLineDiscountColumn =
    defaultDeliveryNoteShowLineDiscountColumn !== false;

  useEffect(() => {
    if (!show) return;
    setPrintShowAmounts(savedShowAmounts);
    setSendShowAmounts(savedShowAmounts);
    setPrintAmountCurrencyMode(savedAmountMode);
    setSendAmountCurrencyMode(savedAmountMode);
    setPrintShowLineDiscountColumn(savedShowLineDiscountColumn);
    setSendShowLineDiscountColumn(savedShowLineDiscountColumn);
  }, [show, savedShowAmounts, savedAmountMode, savedShowLineDiscountColumn]);

  useEffect(() => {
    if (!showSendSettings) return;
    setSendShowAmounts(savedShowAmounts);
    setSendAmountCurrencyMode(savedAmountMode);
    setSendShowLineDiscountColumn(savedShowLineDiscountColumn);
  }, [
    showSendSettings,
    savedShowAmounts,
    savedAmountMode,
    savedShowLineDiscountColumn,
  ]);

  useEffect(() => {
    if (!showPrintSettings) return;
    setPrintShowAmounts(savedShowAmounts);
    setPrintAmountCurrencyMode(savedAmountMode);
    setPrintShowLineDiscountColumn(savedShowLineDiscountColumn);
  }, [
    showPrintSettings,
    savedShowAmounts,
    savedAmountMode,
    savedShowLineDiscountColumn,
  ]);

  if (!order) return null;

  const stateInfo = getApprovalStateInfo(order.approvalState);
  const orderDiscountPercent = (() => {
    const n = Number(order.orderDiscountPercent);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
  })();
  const subtotal = order.subtotal || 0;
  const storedIvaAmount = order.ivaAmount || 0;
  const inferredIvaAmount = (order.total || 0) - subtotal;
  const ivaAmount =
    Math.abs(subtotal + storedIvaAmount - (order.total || 0)) > 0.01
      ? inferredIvaAmount
      : storedIvaAmount;
  const total = order.total || 0;
  const exchangeRate = order.exchangeRate || 1;
  const convertedTotal = total * exchangeRate;
  const getCurrencySymbol = (code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  };
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const referenceSymbol = getCurrencySymbol(referenceCurrency);
  const convertedSymbol = getCurrencySymbol(currency);
  const formatMoney = (value: number) =>
    `${referenceSymbol ? `${referenceSymbol} ` : ''}${numberFormatter.format(
      Number(value) || 0
    )}`;
  const formatConvertedMoney = (value: number) =>
    `${convertedSymbol ? `${convertedSymbol} ` : ''}${numberFormatter.format(
      Number(value) || 0
    )}`;
  const normalizeDiscountPercent = (value: unknown): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
  };
  const lineSubtotal = (
    price: number,
    quantity: number,
    discount: unknown
  ): number =>
    (Number(price) || 0) *
    (Number(quantity) || 0) *
    (1 - normalizeDiscountPercent(discount) / 100) *
    (1 - orderDiscountPercent / 100);
  const { costSubtotal, grossProfit, grossMarginPercent } =
    resolveOrderProfitMetrics({
      subtotal,
      costSubtotal: order.costSubtotal,
      grossProfit: order.grossProfit,
      products: order.products || [],
    });
  const normalizedRecipientEmail = recipientEmail.trim();
  const recipientEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    normalizedRecipientEmail
  );

  const openSendSettings = async () => {
    setShowSendSettings(true);
    setRecipientEmailTouched(false);
    setRecipientEmail('');
    setSendShowAmounts(false);
    setSendAmountCurrencyMode('referenceCurrency');
    if (!onPrepareDeliveryNoteRecipient) {
      return;
    }
    try {
      setLoadingRecipientEmail(true);
      const resolved = (await onPrepareDeliveryNoteRecipient(order)).trim();
      setRecipientEmail(resolved);
    } finally {
      setLoadingRecipientEmail(false);
    }
  };

  const confirmSendDeliveryNote = async () => {
    if (!onSendDeliveryNote || !recipientEmailValid) return;
    try {
      setSendingDeliveryNote(true);
      await onSendDeliveryNote(order, normalizedRecipientEmail, {
        showAmounts: sendShowAmounts,
        amountCurrencyMode: sendAmountCurrencyMode,
        showLineDiscountColumn: sendShowLineDiscountColumn,
      });
      setShowSendSettings(false);
    } finally {
      setSendingDeliveryNote(false);
    }
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={onClose}
        onExited={onExited}
        placement="end"
        scroll={false}
        backdrop
        className={styles.drawer}
        backdropClassName={styles.drawerBackdrop}
      >
        <Offcanvas.Header className={styles.header}>
          <div className={styles.headerRow}>
            <Offcanvas.Title className={styles.headerTitle}>
              Detalles de la Orden
            </Offcanvas.Title>
            <button
              type="button"
              className={styles.headerClose}
              onClick={onClose}
              aria-label="Cerrar"
            >
              <FiX size={22} aria-hidden />
            </button>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.body}>
          <div className={styles.top}>
            <div>
              <p className={styles.saleRef}>
                {order.saleNumber ? `Venta #${order.saleNumber}` : 'Venta'}
                {order.profitPlus2k8?.fact_num != null &&
                Number.isFinite(Number(order.profitPlus2k8.fact_num))
                  ? ` · Profit ${Number(order.profitPlus2k8.fact_num)}`
                  : ''}
              </p>
              <h3 className={styles.clientName}>{order.clientName}</h3>
              <div className={styles.badges}>
                <Badge bg={stateInfo.variant}>{stateInfo.label}</Badge>
                {order.invoiced ? <Badge bg="success">Facturado</Badge> : null}
                {order.deleted ? <Badge bg="danger">Eliminada</Badge> : null}
              </div>
            </div>
            <div className={styles.totalBlock}>
              <span className={styles.totalLabel}>Total</span>
              <span className={styles.totalValue}>{formatMoney(total)}</span>
              <span className={styles.totalSecondary}>
                {formatConvertedMoney(convertedTotal)}
              </span>
            </div>
          </div>

          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Cliente</span>
              <span className={styles.metaValue}>{order.clientName}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Vendedor</span>
              <span className={styles.metaValue}>
                {order.creatorSalesPerson || 'Sin vendedor'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Fecha</span>
              <span className={styles.metaValue}>{formatDate(order.created)}</span>
            </div>
            {(order.recordedAt) && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Registrado</span>
                <span className={styles.metaValue}>
                  {formatDate(order.recordedAt)}
                </span>
              </div>
            )}
            {order.paymentMethod ? (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Método de pago</span>
                <span className={styles.metaValue}>{order.paymentMethod}</span>
              </div>
            ) : null}
            {order.paymentCurrency ? (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Moneda de pago</span>
                <span className={styles.metaValue}>{order.paymentCurrency}</span>
              </div>
            ) : null}
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Tipo de cambio</span>
              <span className={styles.metaValue}>
                {referenceCurrency}→{currency} · {formatExchangeRate(exchangeRate)}
                :1
              </span>
            </div>
            {order.deliveryAddress ? (
              <div className={`${styles.metaItem} ${styles.metaItemWide}`}>
                <span className={styles.metaLabel}>Dirección de entrega</span>
                <span className={styles.metaValue}>{order.deliveryAddress}</span>
              </div>
            ) : null}
          </div>

          {order.comment?.trim() ? (
            <p className={styles.note}>{order.comment.trim()}</p>
          ) : null}

          <p className={styles.sectionLabel}>
            Productos ({order.products.length})
          </p>
          <div className={styles.lines}>
            <div className="table-responsive">
              <Table hover size="sm" className={styles.linesTable}>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="text-end">Cant.</th>
                    <th className="text-end">Precio</th>
                    <th className="text-end">Desc.</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.products.map((product, index) => {
                    const lineDisc = normalizeDiscountPercent(product.discount);
                    const net = lineSubtotal(
                      product.price,
                      product.quantity,
                      product.discount
                    );
                    return (
                      <tr key={index}>
                        <td>
                          <div className={styles.productName}>{product.name}</div>
                          <div className={styles.productCode}>{product.code}</div>
                        </td>
                        <td className="text-end text-nowrap">
                          {product.quantity}
                        </td>
                        <td className="text-end text-nowrap">
                          {formatMoney(product.price)}
                        </td>
                        <td className="text-end text-nowrap">
                          {lineDisc > 0
                            ? `${numberFormatter.format(lineDisc)}%`
                            : '—'}
                        </td>
                        <td className="text-end text-nowrap fw-semibold">
                          {formatMoney(net)}
                        </td>
                      </tr>
                    );
                  })}
                  {order.products.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No hay productos en esta orden
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          <div className={styles.summary}>
            {orderDiscountPercent > 0 ? (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Descuento general</span>
                <span className={styles.summaryValue}>
                  {numberFormatter.format(orderDiscountPercent)}%
                </span>
              </div>
            ) : null}
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Subtotal</span>
              <span className={styles.summaryValue}>{formatMoney(subtotal)}</span>
            </div>
            {ivaAmount > 0 ? (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Impuestos</span>
                <span className={styles.summaryValue}>
                  {formatMoney(ivaAmount)}
                </span>
              </div>
            ) : null}
            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
              <span className={styles.summaryLabel}>Total</span>
              <span className={styles.summaryValue}>{formatMoney(total)}</span>
            </div>
          </div>
          {costSubtotal > 0 ? (
            <p className={styles.summaryHint}>
              Utilidad bruta {formatMoney(grossProfit)}
              {grossMarginPercent != null
                ? ` · margen ${grossMarginPercent.toFixed(1)}%`
                : ''}
            </p>
          ) : null}
        </Offcanvas.Body>
        <div className={styles.footer}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowPrintSettings(true)}
            disabled={!onPrintDeliveryNote}
          >
            Imprimir Nota de Entrega
          </Button>
          <Button
            variant="outline-primary"
            onClick={openSendSettings}
            disabled={!onSendDeliveryNote || sendingDeliveryNote}
          >
            {sendingDeliveryNote ? 'Enviando...' : 'Enviar Nota de Entrega'}
          </Button>
          <Button
            variant={order.invoiced ? 'success' : 'outline-success'}
            onClick={async () => {
              if (!onMarkAsInvoiced || !order) return;
              const profitRetry =
                order.invoiced &&
                isProfitInventoryRetryAvailable?.(order) === true;
              if (order.invoiced && !profitRetry) return;
              try {
                setMarkingInvoiced(true);
                await onMarkAsInvoiced(order);
              } finally {
                setMarkingInvoiced(false);
              }
            }}
            disabled={
              !onMarkAsInvoiced ||
              markingInvoiced ||
              (Boolean(order.invoiced) &&
                !isProfitInventoryRetryAvailable?.(order))
            }
          >
            {order.invoiced
              ? isProfitInventoryRetryAvailable?.(order)
                ? 'Actualizar inventario (Profit)'
                : 'Venta Facturada'
              : markingInvoiced
                ? 'Marcando...'
                : 'Marcar como Facturado'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </Offcanvas>

      <Modal
        show={showPrintSettings}
        onHide={() => setShowPrintSettings(false)}
        centered
        className={styles.nestedModal}
        backdropClassName={styles.nestedModalBackdrop}
      >
        <Modal.Header closeButton>
          <Modal.Title>Opciones de Impresión</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Check
            type="switch"
            id="print-show-amounts"
            label="Mostrar montos en la nota de entrega"
            checked={printShowAmounts}
            onChange={e => setPrintShowAmounts(e.currentTarget.checked)}
            className="mb-3"
          />
          <Form.Check
            type="switch"
            id="print-show-discount-col"
            label="Mostrar columna Desc. %"
            checked={printShowLineDiscountColumn}
            onChange={e =>
              setPrintShowLineDiscountColumn(e.currentTarget.checked)
            }
            className="mb-2"
          />
          <Form.Text className="text-muted d-block mb-3">
            Solo afecta la tabla: puede ocultarse cuando todos los descuentos son
            0 para no confundir. Los montos siempre reflejan descuentos reales.
          </Form.Text>
          {printShowAmounts && (
            <Form.Group>
              <Form.Label>Mostrar montos en</Form.Label>
              <Form.Select
                value={printAmountCurrencyMode}
                onChange={e =>
                  setPrintAmountCurrencyMode(
                    e.currentTarget.value as DeliveryNoteAmountCurrencyMode
                  )
                }
              >
                <option value="referenceCurrency">
                  Moneda de referencia ({referenceCurrency})
                </option>
                <option value="organizationCurrency">
                  Moneda principal ({currency})
                </option>
                <option value="both">
                  Ambas monedas (incluyendo tasa de cambio)
                </option>
              </Form.Select>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPrintSettings(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onPrintDeliveryNote?.(order, {
                showAmounts: printShowAmounts,
                amountCurrencyMode: printAmountCurrencyMode,
                showLineDiscountColumn: printShowLineDiscountColumn,
              });
              setShowPrintSettings(false);
            }}
            disabled={!onPrintDeliveryNote}
          >
            Imprimir
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSendSettings}
        onHide={() => !sendingDeliveryNote && setShowSendSettings(false)}
        centered
        className={styles.nestedModal}
        backdropClassName={styles.nestedModalBackdrop}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar envío de Nota de Entrega</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Confirma el correo de destino antes de enviar.
          </p>
          <Form.Group>
            <Form.Label>Enviar a</Form.Label>
            <Form.Control
              type="email"
              value={recipientEmail}
              onChange={e => {
                setRecipientEmail(e.currentTarget.value);
                setRecipientEmailTouched(true);
              }}
              placeholder={
                loadingRecipientEmail
                  ? 'Buscando correo del cliente...'
                  : 'cliente@empresa.com'
              }
              disabled={loadingRecipientEmail || sendingDeliveryNote}
            />
            {recipientEmailTouched &&
              normalizedRecipientEmail.length > 0 &&
              !recipientEmailValid && (
                <Form.Text className="text-danger">
                  Ingresa un correo valido.
                </Form.Text>
              )}
            {!loadingRecipientEmail &&
              normalizedRecipientEmail.length === 0 &&
              recipientEmailTouched && (
                <Form.Text className="text-danger">
                  El correo es obligatorio para enviar la nota.
                </Form.Text>
              )}
          </Form.Group>
          <hr />
          <Form.Check
            type="switch"
            id="send-show-amounts"
            label="Incluir montos en la nota enviada"
            checked={sendShowAmounts}
            onChange={e => setSendShowAmounts(e.currentTarget.checked)}
            className="mb-3"
            disabled={sendingDeliveryNote}
          />
          <Form.Check
            type="switch"
            id="send-show-discount-col"
            label="Mostrar columna Desc. %"
            checked={sendShowLineDiscountColumn}
            onChange={e =>
              setSendShowLineDiscountColumn(e.currentTarget.checked)
            }
            className="mb-2"
            disabled={sendingDeliveryNote}
          />
          <Form.Text className="text-muted d-block mb-3">
            Solo afecta la tabla: puede ocultarse cuando todos los descuentos son
            0 para no confundir. Los montos siempre reflejan descuentos reales.
          </Form.Text>
          {sendShowAmounts && (
            <Form.Group>
              <Form.Label>Enviar montos en</Form.Label>
              <Form.Select
                value={sendAmountCurrencyMode}
                onChange={e =>
                  setSendAmountCurrencyMode(
                    e.currentTarget.value as DeliveryNoteAmountCurrencyMode
                  )
                }
                disabled={sendingDeliveryNote}
              >
                <option value="referenceCurrency">
                  Moneda de referencia ({referenceCurrency})
                </option>
                <option value="organizationCurrency">
                  Moneda principal ({currency})
                </option>
                <option value="both">
                  Ambas monedas (incluyendo tasa de cambio)
                </option>
              </Form.Select>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowSendSettings(false)}
            disabled={sendingDeliveryNote}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={confirmSendDeliveryNote}
            disabled={
              sendingDeliveryNote || loadingRecipientEmail || !recipientEmailValid
            }
          >
            {sendingDeliveryNote ? 'Enviando...' : 'Confirmar y enviar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrderView;
